import type { Backend, Etag, Option, SyncKey } from "@tento-lona/sheets";
import {
  DateTime,
  MsSinceEpoch,
  NaiveDate,
  SemanticTime,
  Utc,
} from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { LiveData } from "@tento-core/livedata";
import { Rational } from "@tento-core/rational";
import type { TypedApi } from "@tento-core/typed-api";
import { Tracing } from "@tento-core/tracing";
import { Uuid } from "@tento-core/uuid";
import {
  CellId,
  type CellValue,
  Json,
  RowKey,
  RowNetworkKey,
  SheetNetworkKey,
  type TimepointCellEntry,
  UserId,
} from "@tento-lona/sheets";
import { RowsHttpBackend, SyncKeyWatcher } from "@tento-lona/sheets/platform";
import { cellValueWire } from "@tento-lona/sheets/internal-sdk";
import type { CellCardinality, EncodingRegistry } from "@tento-lona/sheets/platform";
import { ListenerErrors, type ListenerErrorSink } from "@tento-core/listener-errors";
import type { SyncClient } from "../services/sync/sync-client.ts";
import { Network } from "../network.ts";
import { RowId } from "@tento-lona/sheets";
import type { SyncPollEntry } from "./sync-poll-entry.ts";
import { CellWriteError, RowWriteError, SdkRequestError } from "../errors.ts";

import { RowSyncState } from "./sync-poll-entry.ts";
import {
  requireDateTimeRange,
  requireNonEmptyKey,
  requireRowId,
} from "./_contracts.ts";
import { RowCellCache } from "./row-cell-cache.ts";
import { IDLE_ROW_CELLS, RowCacheRetention } from "./row-cache-retention.ts";
import { PartialDateMemo, UtcDays } from "@tento-lona/sheets/internal-sdk";

/**
 * Unified backend for cell data. One instance per LonaSdk.Client,
 * shared across all sheets.
 *
 * Sheet-scoped methods take `sheetKey`. Row-scoped methods use RowId.
 *
 * ## Writes
 *
 * Each write verb maps 1:1 to an existing `row_client.rs` endpoint
 * and round-trips per request (no batching) — matches the current
 * per-cell server shape. A later additive `setCells([])` is
 * non-breaking; add when a real call site surfaces latency.
 *
 * The `expectedEtag: Option<Etag>` parameter (`null` today) threads through every
 * mutating verb as no-op plumbing for the 409/optimistic-concurrency
 * path: implementations may pass it as an `If-Match` header once
 * the server grows conflict responses. Today it's accepted and
 * ignored. See `postmortem-2026-04-09-multi-tab-cell-overwrite.toml`.
 */
export interface SyncBackend {
  // ── Sheet-scoped ──
  addRow(
    sheetKey: SheetNetworkKey,
    attrs: Network.NewRowBody,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CreateRowResponse>;
  deleteRow(
    sheetKey: SheetNetworkKey,
    rowKey: RowNetworkKey,
    expectedEtag: Option<Etag>,
  ): Promise<void>;

  // ── Row-scoped ──
  fetchRowCells(
    rowId: RowId,
    range: DateTime.Range<Utc>,
  ): Promise<Network.SheetCell[]>;
  /**
   * `requestId` is a durable write's own id (`DurableHandoffs.nextRequestId`),
   * handed to the debounce executor so a refusal names the writes its
   * execution carried (`DebouncedRequests.Failure.requestIds`); a backend
   * that does not debounce the write ignores it.
   */
  writeCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    data: Option<Json>,
    expectedEtag: Option<Etag>,
    requestId: Option<string>,
  ): Promise<void>;
  /**
   * Clear one date-keyed cell — `Backend.clearCell`'s parameters: `id` names a
   * held id-keyed cell on a multi-cardinality row (`null` on a single one).
   */
  clearCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    id: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<void>;
  writeTimepointCell(
    rowId: RowId,
    dateRange: DateTime.Range<Utc>,
    /** The cell's value: wire JSON, or a canonical `CellValue` (an event
     *  with `DateTime`s) that the HTTP layer encodes with `Json.wire`. */
    data: Option<Json | CellValue>,
    /** Atomic move: id of the old cell the server deletes before
     *  inserting at the new position. Carried as a typed parameter —
     *  the HTTP layer folds it into the wire payload (the server's
     *  rows-side handlers parse it out of `data`). */
    replaceCellId: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CellWriteResponse>;
  /** Delete a multi-cardinality (id-keyed) cell by its cell id — `Backend.clearTimepointCell`'s parameters. */
  clearTimepointCell(
    rowId: RowId,
    cellId: CellId,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CellWriteResponse>;
  updateRow(
    rowKey: RowNetworkKey,
    patch: Network.PatchRow_PendingValidation,
    expectedEtag: Option<Etag>,
  ): Promise<Network.PatchRowResponse>;

  // ── Metadata ──
  rowMeta(rowId: RowId): Option<Network.SheetRow>;
  /**
   * Keep `row` as the row's metadata and apply it to the row's cache.
   * `typeCardinality` is the row type's cell cardinality
   * (`RowType.get(row.type).cellCardinality`), resolved by the caller: the
   * backend layer does not read the row-type registry.
   */
  storeRowMeta(rowId: RowId, row: Network.SheetRow, typeCardinality: CellCardinality): void;

  // ── Sync ──
  applySync(
    rh: RowCellCache,
    syncKeys: readonly SyncKey[],
    range: DateTime.Range<Utc>,
  ): void;
  // ── Debug observability ──
  /**
   * Live map of every sync-key poll this backend has run (`syncKey →
   * entry`), for debug surfaces. `null` for backends that never poll.
   */
  readonly syncPollState: Option<LiveData.Readable<
    ReadonlyMap<SyncKey, SyncPollEntry>
  >>;
}

// ── MemorySyncBackend ─────────────────────────────────────────────

/** Map store entries back to the wire shape for write-response echoes. */
function customCellsToWire(
  cells: readonly TimepointCellEntry[],
): Network.SheetCell[] {
  return cells.map(customCellToWire);
}

function customCellToWire(c: {
  readonly id: string;
  readonly dtr: DateTime.Range<Utc>;
  readonly value: Json | CellValue;
}): Network.SheetCell {
  // An echo of what the store holds, in the wire shape the server answers
  // with: a canonical value is encoded by `cellValueWire` (its `DateTime`s
  // become ISO strings) exactly as `SyncHttpBackend.writeTimepointCell`
  // sends it, and the reader decodes it again (see `RowCellCache.parseCellData`).
  return Network.SheetCellSchema.make({
    id: CellId.of(c.id),
    date: SemanticTime.fromDateTime(c.dtr.start),
    endDate: SemanticTime.fromDateTime(c.dtr.end),
    data: cellValueWire(c.value),
  });
}

/**
 * A `SyncBackend` that also keeps the per-row cell caches (`RowCellCache`) the
 * SDK reads a row's cells through — what every backend the SDK builds is
 * (`SyncHttpBackend`, `MemorySyncBackend` and `SeededSyncBackend`,
 * `SdkBackendAdapter`). Not an implementor seam and on no entry: the caches
 * are the SDK's own, so a `SyncBackend` implementor never builds or names one
 * (the published classes satisfy it by their members, `CellCachingBackend.is`).
 */
export interface CellCachingBackend extends SyncBackend {
  /** The row's one cell cache, built on first use and kept (re-keyed on promote). */
  rowHandle(rowId: RowId): RowCellCache;
  /**
   * The row's cell cache if one exists, without building one (`null`: none).
   * For a feed that only updates caches someone already reads (a store
   * bridge): building a cache per key it sees would keep a cache no sheet
   * binds, which never goes idle, for every key ever written.
   */
  peekRowHandle(rowId: RowId): Option<RowCellCache>;
  /**
   * Re-key a cached row handle (+ its meta) from `from` to `to` IN PLACE,
   * preserving the same `RowCellCache` instance and its loaded cells. Used by the
   * ephemeral→server promote re-key so the one handle per row stays unified
   * across the registry (the accessor and any standalone `_ensureRowHandle`
   * reader converge on it under the server id), not split into a populated
   * old-keyed handle + a fresh empty server-keyed one. No-op when `from === to`
   * or `from` was never cached. See `CellAccessor.rekeyHandles`.
   */
  rekeyRowHandle(from: RowId, to: RowId): void;
  /**
   * Drop every cached row handle and row meta — the client's `dispose`: the
   * handles (and what their closures reach) are released with the client
   * instead of living as long as the backend object. A later `rowHandle`
   * builds a fresh, empty cache.
   */
  releaseRowHandles(): void;
  /**
   * The sheets-side bulk read of a handle reading through this backend (a
   * `parseSheet` preview's `backend`): the sheet-wide read its accessor makes
   * when it loads a range. `SyncHttpBackend` reads the server over its own
   * API; `MemorySyncBackend` holds no server cells, so it answers none;
   * `SeededSyncBackend` answers its seeds.
   */
  sheetReads(): Pick<Backend, "fetchSheetCells">;
}

export namespace CellCachingBackend {
  /** Whether `backend` keeps the SDK's per-row cell caches (one of the SDK's own backends). */
  export function is(backend: SyncBackend): backend is CellCachingBackend {
    return "rowHandle" in backend && typeof backend.rowHandle === "function" &&
      "rekeyRowHandle" in backend && typeof backend.rekeyRowHandle === "function" &&
      "sheetReads" in backend && typeof backend.sheetReads === "function";
  }

  /**
   * `backend` as a caching backend; throws a `TypeError` naming `verb` for a
   * `SyncBackend` the SDK did not build — a handle reads its cells through the
   * backend's per-row caches, which only the SDK's own backends keep.
   */
  export function of(backend: SyncBackend, verb: string): CellCachingBackend {
    if (is(backend)) return backend;
    throw new TypeError(
      `${verb}: a handle reads its cells through the SDK's own backends ` +
        "(SyncHttpBackend, MemorySyncBackend, SeededSyncBackend), which keep its per-row cell caches",
    );
  }
}

/**
 * An in-memory `SyncBackend` for tests and ephemeral sheets: cells and rows live in the process and
 * nothing reaches a server.
 */
export class MemorySyncBackend implements SyncBackend {
  readonly #rowHandleCache = new Map<string, RowCellCache>();
  readonly #rowMetaCache = new Map<string, Network.SheetRow>();
  /** Each stored row's type cardinality (`storeRowMeta`), applied with its metadata. */
  readonly #typeCardinality = new Map<string, CellCardinality>();
  #rowCount = 0;
  /** In-memory backends never poll a sync job. */
  readonly syncPollState = null;

  readonly #realBackend: Option<SyncBackend>;
  readonly #ownerId: UserId;
  /** Where its row caches' subscribers' errors go (the client's `listenerErrorHandler`). */
  readonly #listenerErrors: ListenerErrorSink;
  /** Where a row's cache comes from: built here (`"own"`), or the reading sheet's when it owns its caches elsewhere. */
  readonly #rowCaches: "own" | ((rowId: RowId) => RowCellCache);

  /**
   * `listenerErrors` is the sink every row cache it builds reports a
   * throwing subscriber to: the client's `listenerErrorHandler`
   * (`ListenerErrors.RETHROW` when absent). `rowCaches` is `"own"` (each row's
   * cache is built here) or a resolver that supplies each row's cache instead: a sheet whose reads resolve the
   * client's ordinary caches (`SheetHandleDeps.sharedRowCache`) then finds what
   * this backend wrote (a seed, its coverage) in the very caches it reads, and
   * no second cache per row exists.
   */
  constructor(
    ownerId: UserId,
    realBackend: Option<SyncBackend>,
    listenerErrors: ListenerErrorSink = ListenerErrors.RETHROW,
    rowCaches: "own" | ((rowId: RowId) => RowCellCache) = "own",
  ) {
    this.#ownerId = ownerId;
    this.#realBackend = realBackend;
    this.#listenerErrors = listenerErrors;
    this.#rowCaches = rowCaches;
  }

  /**
   * The sheets-side bulk read (`CellCachingBackend.sheetReads`): no server
   * cells live here, so every sheet-wide read answers no rows and the range
   * reads as covered; the row caches hold what was written.
   */
  sheetReads(): Pick<Backend, "fetchSheetCells"> {
    return { fetchSheetCells: () => Promise.resolve([]) };
  }

  async fetchRowCells(
    rowId: RowId,
    range: DateTime.Range<Utc>,
  ): Promise<Network.SheetCell[]> {
    requireRowId("MemorySyncBackend.fetchRowCells", rowId);
    requireDateTimeRange("MemorySyncBackend.fetchRowCells", "range", range);
    if (this.#realBackend) {
      // Template/catalog rows carry a fresh session UUID but a canonical
      // `lookupKey`: key the server request by that key, NOT the ephemeral
      // session UUID — the UUID isn't a real server row, so it would 401 /
      // read empty. The reserved row materializes server-side off its key.
      const rh = this.#rowHandleCache.get(rowId.toString());
      if (rh?.lookupKey) {
        return this.#realBackend.fetchRowCells(RowId.of(rh.lookupKey), range);
      }
      // The id is ITSELF a canonical shared key (`:~weather:{lid}`,
      // `:~lona:tasks`): it materializes server-side off its own id no
      // matter which sheet hosts it. Without this, such rows on an
      // ephemeral preview sheet read empty (the no-op fetch below).
      if (rowId.isReserved() || rowId.isLookup()) {
        return this.#realBackend.fetchRowCells(rowId, range);
      }
    }
    return [];
  }

  async addRow(
    sheetKey: SheetNetworkKey,
    attrs: Network.NewRowBody,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CreateRowResponse> {
    requireNonEmptyKey("MemorySyncBackend.addRow", "sheetKey", sheetKey);
    const uuid = Uuid.make();
    const id = RowId.fromUuid(uuid);
    const row = Network.CreateRowResponse.make({
      id,
      type: attrs.type,
      ownerId: this.#ownerId,
      timeScale: attrs.timeScale ?? "day",
      position: attrs.position ?? Rational.nth(this.#rowCount++),
      label: attrs.label,
      formula: attrs.formula,
      attributes: attrs.attributes,
      children: null,
      heightPx: null,
      icon: null,
      lookupKey: null,
      metadata: null,
      shouldFetchData: null,
    });
    this.#rowMetaCache.set(id.toString(), row);
    return row;
  }

  async updateRow(
    rowKey: RowNetworkKey,
    patch: Network.PatchRow_PendingValidation,
    expectedEtag: Option<Etag>,
  ): Promise<Network.PatchRowResponse> {
    requireNonEmptyKey("MemorySyncBackend.updateRow", "rowKey", rowKey);
    const existing = this.#rowMetaCache.get(rowKey);
    if (this.#realBackend && existing?.lookupKey) {
      return this.#realBackend.updateRow(rowKey, patch, expectedEtag);
    }
    return { shouldRefetchData: false };
  }

  async deleteRow(
    sheetKey: SheetNetworkKey,
    rowKey: RowNetworkKey,
    expectedEtag: Option<Etag>,
  ): Promise<void> {
    requireNonEmptyKey("MemorySyncBackend.deleteRow", "sheetKey", sheetKey);
    requireNonEmptyKey("MemorySyncBackend.deleteRow", "rowKey", rowKey);
    const meta = this.#rowMetaCache.get(rowKey);
    this.#rowMetaCache.delete(rowKey);
    this.#typeCardinality.delete(rowKey);
    this.#dropUnheld(rowKey);
    if (this.#realBackend && meta?.lookupKey) {
      await this.#realBackend.deleteRow(sheetKey, rowKey, expectedEtag);
    }
  }

  /**
   * A deleted row's cache leaves the registry, or, while an open sheet still
   * holds it (see `SyncHttpBackend.deleteRow`), when its last holder
   * releases it: this backend keeps no idle retention that would drop it
   * later, as `SyncHttpBackend`'s LRU does. A key that has a new cache by
   * then (re-created, re-keyed) keeps it.
   */
  #dropUnheld(rowKey: string): void {
    const deleted = this.#rowHandleCache.get(rowKey);
    if (deleted == null) return;
    const drop = (): void => {
      if (this.#rowHandleCache.get(rowKey) !== deleted) return;
      this.#rowHandleCache.delete(rowKey);
      deleted.dropped();
    };
    if (deleted.fetchBindings.holders === 0) {
      drop();
      return;
    }
    deleted.fetchBindings.observe({
      held: () => {},
      released: () => {
        deleted.fetchBindings.observe(null);
        drop();
      },
    });
  }

  applySync(): void {}

  writeCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    data: Option<Json>,
    expectedEtag: Option<Etag>,
    requestId: Option<string>,
  ): Promise<void> {
    requireRowId("MemorySyncBackend.writeCell", rowId);
    const rh = this.rowHandle(rowId);
    rh.applyExternalEdit(date, data ?? null, null);
    return Promise.resolve();
  }

  clearCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    id: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<void> {
    requireRowId("MemorySyncBackend.clearCell", rowId);
    void expectedEtag;
    this.rowHandle(rowId).applyExternalEdit(date, null, id);
    return Promise.resolve();
  }

  async writeTimepointCell(
    rowId: RowId,
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CellWriteResponse> {
    requireRowId("MemorySyncBackend.writeTimepointCell", rowId);
    requireDateTimeRange(
      "MemorySyncBackend.writeTimepointCell",
      "dateRange",
      dateRange,
    );
    if (replaceCellId != null) {
      requireNonEmptyKey(
        "MemorySyncBackend.writeTimepointCell",
        "replaceCellId",
        replaceCellId,
      );
    }
    // Delegate to real backend for persistent rows. Key the server write by
    // the lookup key, NOT the ephemeral session UUID — the UUID isn't a real
    // server row, so the POST would 401. The reserved row materializes off
    // its lookup key (`POST /api/rows/:~lona:stickers/cells`).
    const rh = this.#rowHandleCache.get(rowId.toString());
    if (rh?.lookupKey && this.#realBackend) {
      return this.#realBackend.writeTimepointCell(
        RowId.of(rh.lookupKey),
        dateRange,
        data,
        replaceCellId,
        expectedEtag,
      );
    }
    // In-memory write — the custom-cell analog of `writeCell`'s
    // `applyExternalEdit`. The store apply happens HERE (caller-
    // independent durability); the response carries only the changed cell
    // because callers reconcile custom writes by id, not by affected range.
    const handle = this.rowHandle(rowId);
    const id = replaceCellId ?? Uuid.make();
    handle.replaceCustomCell(replaceCellId, dateRange, CellId.of(id), data ?? null);
    return {
      affectedStart: SemanticTime.fromDateTime(dateRange.start),
      affectedEnd: SemanticTime.fromDateTime(dateRange.end),
      cells: [
        customCellToWire({
          id,
          dtr: dateRange,
          value: data ?? null,
        }),
      ],
    };
  }

  async clearTimepointCell(
    rowId: RowId,
    cellId: CellId,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CellWriteResponse> {
    requireRowId("MemorySyncBackend.clearTimepointCell", rowId);
    requireNonEmptyKey("MemorySyncBackend.clearTimepointCell", "cellId", cellId);
    // Delegate to real backend for persistent rows — by lookup key, not the
    // ephemeral session UUID (see `writeTimepointCell`).
    const rh = this.#rowHandleCache.get(rowId.toString());
    if (rh?.lookupKey && this.#realBackend) {
      return this.#realBackend.clearTimepointCell(
        RowId.of(rh.lookupKey),
        cellId,
        expectedEtag,
      );
    }
    // In-memory delete: remove the target cell only. Neighboring custom
    // cells survive in the store and do not need to be echoed.
    const handle = this.rowHandle(rowId);
    const dtr = handle.removeCell({ store: "custom", id: cellId });
    if (!dtr) {
      const now = SemanticTime.fromDateTime(
        DateTime.fromMse(MsSinceEpoch.of(Date.now()), DateTime.utc),
      );
      return { affectedStart: now, affectedEnd: now, cells: [] };
    }
    return {
      affectedStart: SemanticTime.fromDateTime(dtr.start),
      affectedEnd: SemanticTime.fromDateTime(dtr.end),
      cells: [],
    };
  }


  peekRowHandle(rowId: RowId): Option<RowCellCache> {
    requireRowId("MemorySyncBackend.peekRowHandle", rowId);
    return this.#rowHandleCache.get(rowId.toString()) ?? null;
  }

  rowHandle(rowId: RowId): RowCellCache {
    requireRowId("MemorySyncBackend.rowHandle", rowId);
    const key = rowId.toString();
    let rh = this.#rowHandleCache.get(key);
    if (!rh && this.#rowCaches !== "own") {
      rh = this.#rowCaches(rowId);
      this.#rowHandleCache.set(key, rh);
      const meta = this.#rowMetaCache.get(key);
      if (meta) rh.applyMeta(meta, this.#typeCardinality.get(key) ?? rh.cardinality);
    }
    if (!rh) {
      // Canonical shared keys (`:~weather:{lid}`, `:~lona:tasks`)
      // materialize server-side off their own id no matter which sheet
      // hosts them, so they get a real fetch that delegates to the real
      // backend (`fetchRow`). The ephemeral session's own uuid / template
      // rows keep the no-op fetch — their cells live only in the in-memory
      // store. Mirrors `SyncHttpBackend.rowHandle`'s `fetchable` guard; without
      // it the no-op's first (empty) fetch optimistically marks the range
      // fetched and suppresses every later real fetch, so reserved rows on
      // an ephemeral preview sheet (e.g. `/sheet/template(weather)`) never
      // load their shared data.
      const fetchable = this.#realBackend != null &&
        (rowId.isReserved() || rowId.isLookup());
      rh = new RowCellCache(
        rowId,
        fetchable ? (range) => this.fetchRowCells(rowId, range) : async () => [], null,
        this.#listenerErrors,
      );
      rh.ephemeral = true;
      this.#rowHandleCache.set(key, rh);
      const meta = this.#rowMetaCache.get(key);
      if (meta) rh.applyMeta(meta, this.#typeCardinality.get(key) ?? rh.cardinality);
    }
    return rh;
  }

  rekeyRowHandle(from: RowId, to: RowId): void {
    requireRowId("MemorySyncBackend.rekeyRowHandle", from);
    requireRowId("MemorySyncBackend.rekeyRowHandle", to);
    const fromKey = from.toString();
    const toKey = to.toString();
    if (fromKey === toKey) return;
    const rh = this.#rowHandleCache.get(fromKey);
    if (rh) {
      this.#rowHandleCache.delete(fromKey);
      this.#rowHandleCache.set(toKey, rh);
    }
    const meta = this.#rowMetaCache.get(fromKey);
    if (meta) {
      this.#rowMetaCache.delete(fromKey);
      this.#rowMetaCache.set(toKey, meta);
    }
    const typeCardinality = this.#typeCardinality.get(fromKey);
    if (typeCardinality != null) {
      this.#typeCardinality.delete(fromKey);
      this.#typeCardinality.set(toKey, typeCardinality);
    }
  }

  /** Drop every cached row handle and row meta (the client's dispose; `CellCachingBackend.releaseRowHandles`). */
  releaseRowHandles(): void {
    this.#rowHandleCache.clear();
    this.#rowMetaCache.clear();
    this.#typeCardinality.clear();
  }

  rowMeta(rowId: RowId): Option<Network.SheetRow> {
    requireRowId("MemorySyncBackend.rowMeta", rowId);
    return this.#rowMetaCache.get(rowId.toString()) ?? null;
  }

  storeRowMeta(rowId: RowId, row: Network.SheetRow, typeCardinality: CellCardinality): void {
    requireRowId("MemorySyncBackend.storeRowMeta", rowId);
    const key = rowId.toString();
    this.#rowMetaCache.set(key, row);
    this.#typeCardinality.set(key, typeCardinality);
    const rh = this.#rowHandleCache.get(key);
    if (rh) rh.applyMeta(row, typeCardinality);
  }
}

// ── SeededSyncBackend ────────────────────────────────────────────────

/**
 * An in-memory backend with pre-seeded cell data — what a template preview
 * or an offline fixture renders from. Extends MemorySyncBackend so
 * rowHandle() returns handles with data already loaded, and fetchCells()
 * returns seeded rows for bulk fetch.
 */
export class SeededSyncBackend extends MemorySyncBackend {
  /**
   * Per seeded row: its cells, each date parsed once at `seedRow`, sorted by
   * first day (`SeededCells`), so a fetch answers by range without parsing.
   */
  readonly #seedData = new Map<string, SeededCells>();
  /** The seeds' date parses, one per distinct date string (judge P8). */
  readonly #dates = new PartialDateMemo(PartialDateMemo.CAPACITY);

  /**
   * Seed cell data for a row before building the sheet: `[date, value]`, or
   * `[date, value, cellId]` for a cell with an id (every `multi`-cardinality
   * row's cell). The shape `EphemeralSheets.SeedCell` publishes, spelled here
   * so the backend names no client type.
   */
  seedRow(rowId: RowId, cells: readonly ([date: string, value: Json] | [date: string, value: Json, cellId: CellId])[]): void {
    const parsed = cells.map(([date, value, cellId], order) => ({
      order,
      date: this.#dates.parse(date).exp(),
      value,
      cellId: cellId ?? null,
    }));
    this.#seedData.set(rowId.toString(), SeededCells.of(parsed, this.#dates));
    // One rebuild + one fire for the whole seed, not one per cell — an
    // 8×60 ephemeral fixture used to trip the fire-listeners witness here.
    const release = this.rowHandle(rowId).deferNotifications();
    try {
      for (const cell of parsed) {
        if (cell.cellId == null) this.writeCell(rowId, cell.date, cell.value, null, null);
        else this.rowHandle(rowId).applyExternalEdit(cell.date, cell.value, cell.cellId);
      }
    } finally {
      release();
    }
  }

  /**
   * The sheets-side bulk read of this backend's seeds: `fetchSheetCells`
   * answers the seeded cells overlapping the day range, per row, in seed
   * order, for whatever sheet asks (the seeds belong to one sheet). A preview
   * or ephemeral sheet on this backend reads its cells through it.
   */
  sheetReads(): Pick<Backend, "fetchSheetCells"> {
    return {
      fetchSheetCells: (_sheetKey, range) =>
        Promise.resolve(this.#seededRows(
          new DateTime.Range(DateTime.fromDse(range.start.dse, Utc), DateTime.fromDse(range.end.dse, Utc)),
        )),
    };
  }

  #seededRows(range: DateTime.Range<Utc>): Awaited<ReturnType<Backend["fetchSheetCells"]>> {
    const result: Network.SheetRowDataResponse[] = [];
    for (const [rowIdStr, seeded] of this.#seedData) {
      const data = seeded.overlapping(range).map((cell) =>
        Network.SheetCellSchema.make({
          id: cell.cellId,
          date: cell.wireDate,
          endDate: null,
          data: cell.value,
        })
      );
      if (data.length > 0) {
        result.push(Network.SheetRowDataResponse.make({
          id: RowId.of(rowIdStr),
          data,
          dataRange: null,
          label: null,
          lookupKey: null,
          metadata: null,
          rowType: null,
          syncKeys: null,
        }));
      }
    }
    return result;
  }
}

/** One seeded cell of a `SeededSyncBackend` row, parsed once. */
class SeededCell {
  #wireDate: Option<SemanticTime> = null;
  /** The seeding backend's date memo (its wire spellings, one per distinct date). */
  readonly #dates: PartialDateMemo;

  constructor(
    /** Position in the seed (a fetch answers in seed order). */
    readonly order: number,
    readonly date: NaiveDate.Partial,
    readonly value: Json,
    /** The cell's id (every `multi`-cardinality row's cell has one); `null` when seeded without. */
    readonly cellId: Option<CellId>,
    dates: PartialDateMemo,
  ) {
    this.#dates = dates;
  }

  /** First day (dse) the cell covers. */
  get startDse(): number {
    return this.date.start.dse;
  }

  /** Day (dse) after the last it covers. */
  get endDse(): number {
    return this.date.end.dse;
  }

  /** The wire spelling of the date, built the first time a read answers the cell. */
  get wireDate(): SemanticTime {
    this.#wireDate ??= this.#dates.semanticTime(this.date.toString());
    return this.#wireDate;
  }
}

const MS_PER_DAY = 86_400_000;

/** A seeded row's cells sorted by first day, answering overlap reads by binary search. */
class SeededCells {
  private constructor(
    readonly byStart: readonly SeededCell[],
    /** The longest span (days) of any cell: how far back an overlapping cell can start. */
    readonly longestDays: number,
  ) {}

  static of(
    cells: readonly {
      readonly order: number;
      readonly date: NaiveDate.Partial;
      readonly value: Json;
      readonly cellId: Option<CellId>;
    }[],
    dates: PartialDateMemo,
  ): SeededCells {
    let longest = 0;
    let sorted = true;
    let prevStart = Number.NEGATIVE_INFINITY;
    const byStart = cells.map((c): SeededCell => {
      const cell = new SeededCell(c.order, c.date, c.value, c.cellId, dates);
      const start = cell.startDse;
      longest = Math.max(longest, cell.endDse - start);
      if (start < prevStart) sorted = false;
      prevStart = start;
      return cell;
    });
    // A seed is usually in date order already; sort (stably, by first day) only when not.
    if (!sorted) byStart.sort((a, b) => a.startDse - b.startDse || a.order - b.order);
    return new SeededCells(byStart, longest);
  }

  /** The cells whose days overlap `range` (UTC), in seed order. */
  overlapping(range: DateTime.Range<Utc>): SeededCell[] {
    const cells = this.byStart;
    const from = range.start.mse / MS_PER_DAY - this.longestDays;
    let lo = 0;
    let hi = cells.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((cells[mid]?.startDse ?? 0) < from) lo = mid + 1;
      else hi = mid;
    }
    const out: SeededCell[] = [];
    for (let i = lo; i < cells.length; i++) {
      const cell = cells[i];
      if (cell === undefined || cell.startDse * MS_PER_DAY >= range.end.mse) break;
      if (cell.endDse * MS_PER_DAY > range.start.mse) out.push(cell);
    }
    return out.sort((a, b) => a.order - b.order);
  }
}

// ── SyncHttpBackend ──────────────────────────────────────────────────

/** The `SyncBackend` that reads and writes cells over the server's REST API. */
export class SyncHttpBackend<
  I extends Record<string, unknown> = Record<never, never>,
> implements SyncBackend {
  readonly #rowHandleCache = new Map<string, RowCellCache>();
  /**
   * The rows no open sheet holds, in an LRU bounded by cell count: past it the
   * least recently used is dropped from `#rowHandleCache` with its cells and
   * fetch coverage, and a later read fetches it again (`RowCacheRetention`).
   */
  readonly #retention = new RowCacheRetention(IDLE_ROW_CELLS, (key, rh) => {
    if (this.#rowHandleCache.get(key) === rh) this.#rowHandleCache.delete(key);
    rh.dropped();
  });
  readonly #rowMetaCache = new Map<string, Network.SheetRow>();
  /** Each stored row's type cardinality (`storeRowMeta`), applied with its metadata. */
  readonly #typeCardinality = new Map<string, CellCardinality>();
  readonly #activePolls = new Map<SyncKey, SyncPollEntry>();
  readonly #pollStates = new LiveData<
    ReadonlyMap<SyncKey, SyncPollEntry>
  >(new Map());
  readonly syncPollState: LiveData.Readable<
    ReadonlyMap<SyncKey, SyncPollEntry>
  > = this.#pollStates;
  readonly #api: TypedApi<Network.Routes>;
  readonly #sync: Option<SyncClient>;
  /** Where its row caches' subscribers' errors go (the client's `listenerErrorHandler`). */
  readonly #listenerErrors: ListenerErrorSink;
  readonly #encodings: EncodingRegistry;

  /**
   * A backend over `api`, polling sync keys through `sync` (`null`: no sync
   * polling), decoding every row handle's cells through `encodings` — the
   * client's registry (`EncodingRegistry.withBuiltins()` for a fresh one
   * holding the built-in decoders).
   */
  constructor(
    api: TypedApi<Network.Routes>,
    sync: Option<SyncClient>,
    encodings: EncodingRegistry,
    listenerErrors: ListenerErrorSink = ListenerErrors.RETHROW,
  ) {
    this.#api = api;
    this.#sync = sync;
    this.#encodings = encodings;
    this.#listenerErrors = listenerErrors;
  }

  async addRow(
    sheetKey: SheetNetworkKey,
    attrs: Network.NewRowBody,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CreateRowResponse> {
    requireNonEmptyKey("SyncHttpBackend.addRow", "sheetKey", sheetKey);
    // after-failed-write: the sync backend is the wire layer below the cell and row writers, which recover.
    return RowWriteError.check(
      "add",
      "POST",
      "/api/sheets/{sheetKey}/row",
      () =>
        this.#api.postResult("/api/sheets/{sheetKey}/row", {
          path: { sheetKey },
          body: attrs,
        }),
    );
  }

  async updateRow(
    rowKey: RowNetworkKey,
    patch: Network.PatchRow_PendingValidation,
    expectedEtag: Option<Etag>,
  ): Promise<Network.PatchRowResponse> {
    requireNonEmptyKey("SyncHttpBackend.updateRow", "rowKey", rowKey);
    return RowWriteError.check(
      "update",
      "PATCH",
      "/api/rows/{rowKey}",
      () => this.#api.patchResult("/api/rows/{rowKey}", { path: { rowKey: RowNetworkKey.fromWire(rowKey) }, body: patch }),
    );
  }

  async deleteRow(
    sheetKey: SheetNetworkKey,
    rowKey: RowNetworkKey,
    expectedEtag: Option<Etag>,
  ): Promise<void> {
    requireNonEmptyKey("SyncHttpBackend.deleteRow", "sheetKey", sheetKey);
    requireNonEmptyKey("SyncHttpBackend.deleteRow", "rowKey", rowKey);
    // Checked before the caches drop the row: a refused delete leaves the
    // row's handle and metadata in place.
    await RowWriteError.check(
      "delete",
      "DELETE",
      "/api/sheets/{sheetKey}/row/{rowKey}",
      () =>
        this.#api.delResult("/api/sheets/{sheetKey}/row/{rowKey}", {
          path: { sheetKey, rowKey: RowNetworkKey.fromWire(rowKey) },
        }),
    );
    // A cache an open sheet still holds stays registered (the sheet reads
    // and releases it; released, it goes idle and the LRU evicts it): dropped
    // now, the next `rowHandle` of the key would build a second cache beside
    // the one the sheet holds.
    const deleted = this.#rowHandleCache.get(rowKey);
    if (deleted != null && deleted.fetchBindings.holders === 0) {
      this.#retention.forget(deleted);
      this.#rowHandleCache.delete(rowKey);
      deleted.dropped();
    }
    this.#rowMetaCache.delete(rowKey);
    this.#typeCardinality.delete(rowKey);
  }

  /**
   * The sheets-side bulk read (`CellCachingBackend.sheetReads`) over this
   * backend's API: the same required read as the client's `RowsHttpBackend`,
   * so a preview reading through this backend loads a range from the server
   * it was given.
   */
  sheetReads(): Pick<Backend, "fetchSheetCells"> {
    const rows = new RowsHttpBackend(this.#api);
    return { fetchSheetCells: (sheetKey, range) => rows.fetchSheetCells(sheetKey, range) };
  }

  async fetchRowCells(
    rowId: RowId,
    range: DateTime.Range<Utc>,
  ): Promise<Network.SheetCell[]> {
    requireRowId("SyncHttpBackend.fetchRowCells", rowId);
    requireDateTimeRange("SyncHttpBackend.fetchRowCells", "range", range);
    return this.#fetchRowCells(rowId, range);
  }

  /** The row's cache if one exists (`CellCachingBackend.peekRowHandle`): builds none, and does not count as a read for the idle LRU. */
  peekRowHandle(rowId: RowId): Option<RowCellCache> {
    requireRowId("SyncHttpBackend.peekRowHandle", rowId);
    return this.#rowHandleCache.get(rowId.toString()) ?? null;
  }

  rowHandle(rowId: RowId): RowCellCache<I> {
    requireRowId("SyncHttpBackend.rowHandle", rowId);
    const key = rowId.toString();
    let rh = this.#rowHandleCache.get(key);
    if (!rh) {
      // Only ids the cells route can serve hit the network. Virtual /
      // pending / template / system ids are client-synthesized — their
      // cells arrive via local writes or parent delegation, and a GET
      // for them would 404. Mirrors `CellAccessor._makeFetchFn`'s
      // guard so the registry can host every id kind.
      const fetchable = rowId.isUser() || rowId.isLookup() ||
        rowId.isReserved();
      rh = new RowCellCache(
        rowId,
        fetchable
          ? (range) => this.#fetchRowCells(rowId, range)
          : async () => [],
        this.#encodings,
        this.#listenerErrors,
      );
      this.#rowHandleCache.set(key, rh);
      this.#retention.track(key, rh);
      const meta = this.#rowMetaCache.get(key);
      if (meta) rh.applyMeta(meta, this.#typeCardinality.get(key) ?? rh.cardinality);
    } else {
      this.#retention.touch(rh);
    }
    return rh as RowCellCache<I>;
  }

  rekeyRowHandle(from: RowId, to: RowId): void {
    requireRowId("SyncHttpBackend.rekeyRowHandle", from);
    requireRowId("SyncHttpBackend.rekeyRowHandle", to);
    const fromKey = from.toString();
    const toKey = to.toString();
    if (fromKey === toKey) return;
    const rh = this.#rowHandleCache.get(fromKey);
    if (rh) {
      this.#rowHandleCache.delete(fromKey);
      this.#rowHandleCache.set(toKey, rh);
      this.#retention.rekey(rh, toKey);
    }
    const meta = this.#rowMetaCache.get(fromKey);
    if (meta) {
      this.#rowMetaCache.delete(fromKey);
      this.#rowMetaCache.set(toKey, meta);
    }
    const typeCardinality = this.#typeCardinality.get(fromKey);
    if (typeCardinality != null) {
      this.#typeCardinality.delete(fromKey);
      this.#typeCardinality.set(toKey, typeCardinality);
    }
  }

  /** Write one date-keyed cell; throws `CellWriteError("write-cell")`. */
  async writeCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    data: Option<Json>,
    expectedEtag: Option<Etag>,
    requestId: Option<string>,
  ): Promise<void> {
    requireRowId("SyncHttpBackend.writeCell", rowId);
    // after-failed-write: the sync backend is the wire layer below the cell and row writers, which recover.
    await CellWriteError.check(
      "write-cell",
      "POST",
      "/api/rows/{rowKey}/cells",
      () =>
        this.#api.postResult("/api/rows/{rowKey}/cells", {
          path: { rowKey: RowNetworkKey.of(rowId) },
          body: Network.CellWriteRequest.make({
            date: SemanticTime.fromPartialDate(date),
            // Legacy wire shape: an object (or `null`) travels as its JSON
            // text; `clearCell` is the path that writes a real JSON null.
            data: typeof data === "object" ? JSON.stringify(data) : data,
            endDate: null,
            id: null,
          }),
        }),
    );
  }

  /**
   * Clear one date-keyed cell (a held id-keyed one by its `id`); throws
   * `CellWriteError("clear-cell")`.
   */
  async clearCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    id: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<void> {
    requireRowId("SyncHttpBackend.clearCell", rowId);
    // Deliberately skip writeCell's legacy stringify path — clearing
    // writes a real JSON null, not the string "null".
    // after-failed-write: the sync backend is the wire layer below the cell and row writers, which recover.
    await CellWriteError.check(
      "clear-cell",
      "POST",
      "/api/rows/{rowKey}/cells",
      () =>
        this.#api.postResult("/api/rows/{rowKey}/cells", {
          path: { rowKey: RowNetworkKey.of(rowId) },
          body: Network.CellWriteRequest.make({
            date: SemanticTime.fromPartialDate(date),
            data: null,
            endDate: null,
            id,
          }),
        }),
    );
  }

  writeTimepointCell(
    rowId: RowId,
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CellWriteResponse> {
    return postTimepointCell(this.#api, rowId, dateRange, data, replaceCellId, expectedEtag);
  }

  clearTimepointCell(
    rowId: RowId,
    cellId: CellId,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CellWriteResponse> {
    return postTimepointClear(this.#api, rowId, cellId, expectedEtag);
  }


  /** Drop every cached row handle and row meta (the client's dispose; `CellCachingBackend.releaseRowHandles`). */
  releaseRowHandles(): void {
    this.#retention.clear();
    this.#rowHandleCache.clear();
    this.#rowMetaCache.clear();
    this.#typeCardinality.clear();
  }

  rowMeta(rowId: RowId): Option<Network.SheetRow> {
    requireRowId("SyncHttpBackend.rowMeta", rowId);
    return this.#rowMetaCache.get(rowId.toString()) ?? null;
  }

  storeRowMeta(rowId: RowId, row: Network.SheetRow, typeCardinality: CellCardinality): void {
    requireRowId("SyncHttpBackend.storeRowMeta", rowId);
    const key = rowId.toString();
    this.#rowMetaCache.set(key, row);
    this.#typeCardinality.set(key, typeCardinality);
    const rh = this.#rowHandleCache.get(key);
    if (rh) rh.applyMeta(row, typeCardinality);
  }

  async #fetchRowCells(
    rowId: RowId,
    range: DateTime.Range<Utc>,
  ): Promise<Network.SheetCell[]> {
    requireRowId("SyncHttpBackend.fetchRowCells", rowId);
    requireDateTimeRange("SyncHttpBackend.fetchRowCells", "range", range);
    const rowKey = RowNetworkKey.forRead(rowId);
    // Every UTC day the range touches (the read's days are end-exclusive): never drop the day it ends in.
    const days = UtcDays.touched(range);
    const query = { start: days.start, end: days.end, mock_sync_delay: null, booking_id: null };
    const path = "/api/rows/{rowKey}/cells";
    return await SdkRequestError.checkRequest(
      "cells",
      "get-cells",
      "GET",
      path,
      () => this.#api.getResult(path, { path: { rowKey }, query }),
    );
  }

  /**
   * Start polling for sync job completion.
   *
   * Called by CellAccessor when fetchCells returns sync keys. This can be
   * called repeatedly for the same row (e.g. on every scroll-triggered
   * fetch), so we dedup at two levels:
   *
   * 1. Terminal dedup — keys whose poll already finished (complete/failed)
   *    are filtered out entirely. Without this, scrolling back to a weather
   *    row would reset sync state for already-synced data.
   * 2. Active dedup — keys already being polled (pending/running in
   *    activePolls) are skipped via the `has` check in the loop.
   *
   * Poll entries persist in activePolls after completion (not deleted),
   * which is what enables terminal dedup. The map is published to
   * syncPollState (LiveData) for debug observability.
   *
   * `range` is accepted for interface compat but unused — the previous
   * behavior of re-fetching the row on sync complete was removed because
   * the render fix (previousRenderState) means cells render correctly
   * from cached data without a forced re-fetch.
   */
  applySync(
    rh: RowCellCache,
    syncKeys: readonly SyncKey[],
    range: DateTime.Range<Utc>,
  ): void {
    if (!this.#sync) return;

    const newKeys: SyncKey[] = [];
    for (const key of syncKeys) {
      const existing = this.#activePolls.get(key);
      if (
        existing &&
        (existing.status === "complete" || existing.status === "failed")
      ) {
        continue;
      }
      newKeys.push(key);
    }

    if (newKeys.length === 0) return;

    const state = RowSyncState.fromKeys(newKeys);
    rh.setSyncState(state);
    for (const key of newKeys) {
      if (this.#activePolls.has(key)) continue;
      const ks = nonnull(state.syncKeys.get(key), `RowSyncState.fromKeys holds ${key}`);
      this.#pollSyncKey(this.#sync, rh, ks, state);
    }
  }

  #pollSyncKey(
    syncClient: SyncClient,
    rh: RowCellCache,
    ks: { syncKey: SyncKey; status: string },
    state: RowSyncState,
  ): void {
    // The inner loop already handles transient poll failures and marks the key
    // "failed" on exhaustion; a throw escaping to here is an unexpected driver
    // crash. Surface it loudly — non-fatal, the poll just stops.
    void this.#pollSyncKeyAsync(syncClient, rh, ks, state).catch((err) =>
      Tracing.err("net")("sync-poll driver crashed", {
        syncKey: ks.syncKey,
        error: String(err),
      }),
    );
  }

  #publishPollState(): void {
    this.#pollStates.update(new Map(this.#activePolls));
  }

  #completePoll(
    rh: RowCellCache,
    state: RowSyncState,
    entry: SyncPollEntry,
    status: "complete" | "failed",
  ): void {
    entry.status = status;
    entry.end = DateTime.utc.now();
    this.#publishPollState();
    RowSyncState.updateAggregate(state);
    if (RowSyncState.allTerminal(state)) rh.setSyncState(null);
    else rh.setSyncState(state);
  }

  /**
   * Poll one key through the client-wide `SyncKeyWatcher` (the same watcher
   * the rows-side sheets use, keyed on `SyncClient.poll`), so this path gets
   * the one-loop-per-key dedup, the client-wide pace, and the 429 pause. Its
   * own loop polled every 500ms for 5s per key with no cross-key limit and
   * retried a 429 on that cadence.
   */
  async #pollSyncKeyAsync(
    syncClient: SyncClient,
    rh: RowCellCache,
    ks: { syncKey: SyncKey; status: string },
    state: RowSyncState,
  ): Promise<void> {
    const stage = state.syncKeys.get(ks.syncKey)?.stage ?? null;
    const entry: SyncPollEntry = {
      syncKey: ks.syncKey,
      status: "pending",
      start: DateTime.utc.now(),
      end: null,
      stage,
    };
    this.#activePolls.set(ks.syncKey, entry);
    this.#publishPollState();

    try {
      const lease = SyncKeyWatcher.for(syncClient.poll).watch(
        ks.syncKey,
        (status) => {
          if (status === "complete" || status === "failed") return;
          ks.status = status;
          entry.status = status;
          this.#publishPollState();
          RowSyncState.updateAggregate(state);
          rh.setSyncState(state);
        },
      );
      const outcome = await lease.outcome;
      // Released: the client was disposed (its watcher stopped every key),
      // so there is no verdict to show.
      if (outcome === "released") return;
      // "expired" (the watcher gave up) reads as failed, as the old loop's
      // exhausted attempts did.
      const terminal = outcome === "complete" ? "complete" : "failed";
      ks.status = terminal;
      this.#completePoll(rh, state, entry, terminal);
    } finally {
      this.#publishPollState();
    }
  }
}

/**
 * One timepoint cell write on the wire (`POST /api/rows/{rowKey}/cells`
 * with a `date` / `endDate` range) through `api` — the HTTP backend's
 * `writeTimepointCell`, also the route of a promoted ephemeral sheet's
 * timepoint writes (`CellAccessor.setCustom`). Checked: a refusal throws
 * `CellWriteError("write-timepoint-cell")`.
 *
 * @internal SDK-private (`SyncHttpBackend`, `CellAccessor`); no barrel exports it.
 */
export function postTimepointCell(
  api: TypedApi<Network.Routes>,
  rowId: RowId,
  dateRange: DateTime.Range<Utc>,
  data: Option<Json | CellValue>,
  replaceCellId: Option<CellId>,
  expectedEtag: Option<Etag>,
): Promise<Network.CellWriteResponse> {
  requireRowId("SyncHttpBackend.writeTimepointCell", rowId);
  requireDateTimeRange("SyncHttpBackend.writeTimepointCell", "dateRange", dateRange);
  void expectedEtag;
  if (replaceCellId != null) {
    requireNonEmptyKey(
      "SyncHttpBackend.writeTimepointCell",
      "replaceCellId",
      replaceCellId,
    );
  }
  // Wire boundary: the server's rows-side handlers read
  // `replaceCellId` out of the `data` payload, so it is folded in
  // HERE — everywhere above this layer it travels as a typed
  // parameter, never smuggled inside the payload. `cellValueWire` is the
  // encoder: a canonical value's `DateTime`s become their ISO strings,
  // exactly as `JSON.stringify` writes them, and a canonical event's
  // `null` extras are left out, as the stored cell has them.
  const wire = cellValueWire(data);
  const wireData = replaceCellId != null
    ? { ...(Json.object(wire) ?? {}), replaceCellId }
    : wire;
  // after-failed-write: the sync backend is the wire layer below the cell and row writers, which recover.
  return CellWriteError.check(
    "write-timepoint-cell",
    "POST",
    "/api/rows/{rowKey}/cells",
    () =>
      api.postResult("/api/rows/{rowKey}/cells", {
        path: { rowKey: RowNetworkKey.of(rowId) },
        body: Network.CellWriteRequest.make({
          date: SemanticTime.fromDateTime(dateRange.start),
          endDate: SemanticTime.fromDateTime(dateRange.end),
          data: wireData,
          id: null,
        }),
      }),
  );
}

/**
 * One timepoint cell delete on the wire (`id` + `data: null`, which the
 * server dispatches to the row's delete handler) through `api` — the
 * HTTP backend's `clearTimepointCell` (see `postTimepointCell`). Checked:
 * a refusal throws `CellWriteError("clear-timepoint-cell")`.
 *
 * @internal SDK-private (`SyncHttpBackend`, `CellAccessor`); no barrel exports it.
 */
export function postTimepointClear(
  api: TypedApi<Network.Routes>,
  rowId: RowId,
  cellId: CellId,
  expectedEtag: Option<Etag>,
): Promise<Network.CellWriteResponse> {
  requireRowId("SyncHttpBackend.clearTimepointCell", rowId);
  void expectedEtag;
  requireNonEmptyKey("SyncHttpBackend.clearTimepointCell", "cellId", cellId);
  // after-failed-write: the sync backend is the wire layer below the cell and row writers, which recover.
  return CellWriteError.check(
    "clear-timepoint-cell",
    "POST",
    "/api/rows/{rowKey}/cells",
    () =>
      api.postResult("/api/rows/{rowKey}/cells", {
        path: { rowKey: RowNetworkKey.of(rowId) },
        body: Network.CellWriteRequest.make({
          id: cellId,
          data: null,
          date: null,
          endDate: null,
        }),
      }),
  );
}
